import React, { useState, useEffect } from 'react';
import { linkifyText } from '../utils/linkify';
import { 
  Box, 
  Typography, 
  Divider, 
  List, 
  ListItem, 
  TextField, 
  Button, 
  Paper, 
  Avatar,
  CircularProgress,
  Fade,
  Zoom,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  FormControlLabel,
  Checkbox,
  Tooltip
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Comment } from '../types';
import { formatDate } from '../utils/formatDate';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useSubmissionWindow } from '../hooks/useSubmissionWindow';
import LoginForm from './LoginForm';
import { api } from '../services/api';
import { ADMIN_EMAILS } from '../constants'; // Import from constants

const TEST_USER_ID = process.env.REACT_APP_TEST_USER_ID || "00000000-0000-0000-0000-000000000001";

// Define admin emails here - REMOVED
// const ADMIN_EMAILS = [
//   'rporter@mpactcollective.com',
//   'iomara@mpactcollective.com',
//   'josh@endstation.us',
//   'peleke@endstation.us',
// ];

interface CommentsSectionProps {
  comments: Comment[];
  ideaId: string;
  loading: boolean;
  onAddComment: (ideaId: string, text: string, isAnonymous?: boolean) => Promise<void>;
  onDeleteComment?: (commentId: string) => Promise<void>;
}

const CommentsSection: React.FC<CommentsSectionProps> = ({
  comments,
  ideaId,
  loading,
  onAddComment,
  onDeleteComment,
}) => {
  const { isAuthenticated, currentUser } = useAuth();
  const { tenantConfig } = useTenant();
  const { locale, t } = useLanguage();
  const { isClosed: submissionsClosed } = useSubmissionWindow();

  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);

  // Helper function to check if the current user is an admin
  const isCurrentUserAdmin = (): boolean => {
    return !!currentUser?.email && ADMIN_EMAILS.includes(currentUser.email);
  };

  const validateForm = () => {
    if (!commentText.trim()) {
      setError(t.comments.commentEmpty);
      return false;
    }
    
    return true;
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    if (!isAuthenticated) {
      setShowLoginForm(true);
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      await onAddComment(ideaId, commentText, isAnonymous);
      setCommentText('');
      setIsAnonymous(false); // Reset anonymous checkbox after posting
    } catch (err) {
      setError(t.comments.submitFailed);
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Get a deterministic color for an avatar based on the commenter's name
  const getAvatarColor = (name: string) => {
    const colors = [
      '#e57373', '#f06292', '#ba68c8', '#9575cd', 
      '#7986cb', '#64b5f6', '#4fc3f7', '#4dd0e1', 
      '#4db6ac', '#81c784', '#aed581', '#dce775',
      '#fff176', '#ffd54f', '#ffb74d', '#ff8a65'
    ];
    
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = ((hash << 5) - hash) + name.charCodeAt(i);
      hash |= 0;
    }
    
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const handleDeleteClick = (commentId: string) => {
    setCommentToDelete(commentId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!commentToDelete || !onDeleteComment) return;
    
    try {
      setDeleting(true);
      await onDeleteComment(commentToDelete);
      setDeleteDialogOpen(false);
      setCommentToDelete(null);
    } catch (err) {
      console.error('Failed to delete comment:', err);
      setError('Failed to delete comment. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setCommentToDelete(null);
  };

  const handleCloseLoginForm = () => {
    setShowLoginForm(false);
  };

  // Get display name for a user_id
  const getDisplayName = (comment: Comment): string => {
    return comment.user_display_name || comment.display_name || comment.user_id;
  };

  // Get the first letter for avatar
  const getAvatarLetter = (comment: Comment): string => {
    const displayName = getDisplayName(comment);
    return displayName.charAt(0).toUpperCase();
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography 
        variant="h5" 
        sx={{ 
          mb: 2, 
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {t.comments.title}
        <Box
          component="span" 
          sx={{ 
            ml: 2, 
            bgcolor: 'primary.main', 
            color: 'white', 
            borderRadius: '12px',
            px: 1.5,
            py: 0.5,
            fontSize: '0.9rem',
            fontWeight: 'bold',
          }}
        >
          {comments.length}
        </Box>
      </Typography>
      
      <Divider sx={{ mb: 3 }} />
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress size={30} />
        </Box>
      ) : (
        <>
          {comments.length === 0 ? (
            <Fade in={true} timeout={700}>
              <Paper 
                sx={{ 
                  p: 4, 
                  textAlign: 'center', 
                  mb: 3,
                  borderStyle: 'dashed',
                  borderWidth: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.default',
                }}
              >
                <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t.comments.noComments}
                </Typography>
              </Paper>
            </Fade>
          ) : (
            <List sx={{ mb: 4 }}>
              {comments.map((comment, index) => (
                <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }} key={comment.id}>
                  <ListItem 
                    alignItems="flex-start"
                    sx={{ 
                      mb: 2, 
                      p: 0,
                      display: 'block',
                    }}
                  >
                    <Paper 
                      elevation={1} 
                      sx={{ 
                        p: 2, 
                        borderRadius: '12px',
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '4px',
                          height: '100%',
                          backgroundColor: getAvatarColor(comment.id),
                          borderTopLeftRadius: '12px',
                          borderBottomLeftRadius: '12px',
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex' }}>
                        <Avatar 
                          sx={{ 
                            mr: 2, 
                            bgcolor: getAvatarColor(comment.id), 
                            width: 40, 
                            height: 40,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        >
                          {getAvatarLetter(comment)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              {getDisplayName(comment)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(comment.created_at, locale)}
                              </Typography>
                              {isCurrentUserAdmin() && onDeleteComment && (
                                <IconButton 
                                  size="small" 
                                  color="error"
                                  onClick={() => handleDeleteClick(comment.id)}
                                  sx={{ ml: 1 }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                          <Typography 
                            variant="body2" 
                            color="text.primary" 
                            sx={{ 
                              mt: 1.5,
                              lineHeight: 1.6,
                              wordBreak: 'break-word',
                            }}
                          >
                            {linkifyText(comment.comment_text)}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </ListItem>
                </Zoom>
              ))}
            </List>
          )}
          
          {submissionsClosed ? (
            <Alert severity="info" icon={false} sx={{ mt: 3, borderRadius: '12px' }}>
              {t.submissionWindow.commentsClosed}
            </Alert>
          ) : (
            <Fade in={true} timeout={1000}>
              <Paper 
                sx={{ 
                  p: 3, 
                  mt: 3,
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                }}
              >
                {showLoginForm ? (
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {t.comments.loginToPostTitle}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t.comments.loginToPostSubtitle}
                    </Typography>
                    <LoginForm onClose={handleCloseLoginForm} />
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                      <Button 
                        variant="text" 
                        color="primary" 
                        onClick={handleCloseLoginForm}
                      >
                        {t.comments.backToCommentForm}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <>
                    <Typography variant="h6" gutterBottom>
                      {t.comments.addYourComment}
                    </Typography>
                  
                    {isAuthenticated && (
                      <Alert severity="info" sx={{ mb: 3 }}>
                        {t.comments.postAsPrefix}{' '}
                        <strong>{currentUser?.displayName || currentUser?.email}</strong>
                      </Alert>
                    )}
                  
                    <Box component="form" onSubmit={handleCommentSubmit}>
                      <TextField
                        label={t.comments.yourComment}
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        required
                        multiline
                        rows={4}
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        error={!!error}
                        helperText={error || ""}
                        placeholder={t.comments.commentPlaceholder}
                        InputProps={{
                          sx: { borderRadius: '8px' }
                        }}
                      />
                    
                      {/* Anonymous Comment Option - Only for Evanston tenant */}
                      {tenantConfig?.tenant_id === 'evanston' && (
                        <Box sx={{ mt: 1, mb: 2 }}>
                          <Tooltip 
                            title={isAnonymous
                              ? t.comments.anonymousTooltipOn
                              : t.ideaForm.anonymousTooltipOff
                            }
                            arrow
                            placement="right"
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isAnonymous}
                                  onChange={(e) => setIsAnonymous(e.target.checked)}
                                  disabled={submitting}
                                  size="small"
                                  icon={<VisibilityIcon fontSize="small" color="action" />}
                                  checkedIcon={<VisibilityOffIcon fontSize="small" color="primary" />}
                                />
                              }
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="body2">
                                    {isAnonymous
                                      ? t.comments.commentingAnonymously
                                      : `${t.comments.commentingAsPrefix} ${currentUser?.displayName || currentUser?.email || ''}`
                                    }
                                  </Typography>
                                  {isAnonymous && (
                                    <Typography variant="caption" color="text.secondary">
                                      {t.ideaForm.adminsCanSee}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </Tooltip>
                        </Box>
                      )}
                    
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button 
                          type="submit" 
                          variant="contained" 
                          color="primary"
                          disabled={submitting}
                          endIcon={submitting ? undefined : <SendIcon />}
                          sx={{ 
                            px: 3,
                            py: 1,
                            borderRadius: '8px',
                            fontWeight: 'bold',
                          }}
                        >
                          {submitting ? <CircularProgress size={24} /> : isAuthenticated ? t.comments.postComment : t.comments.loginToPost}
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
              </Paper>
            </Fade>
          )}
          
          {/* Delete confirmation dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={handleCancelDelete}
          >
            <DialogTitle>
              Delete Comment
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete this comment? This action cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancelDelete} color="primary">
                Cancel
              </Button>
              <Button 
                onClick={handleConfirmDelete} 
                color="error" 
                variant="contained"
                disabled={deleting}
                startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default CommentsSection; 